import { AlertColor, AlertProps, AlertTitle, Alert as MuiAlert, Snackbar, SnackbarProps } from "@mui/material";
import React, { useContext } from "react";

type Message = {
    value: React.ReactNode;
    title?: React.ReactNode;
    severity?: AlertColor;
};

type Alert = {
    message: Message;
    AlertProps?: Omit<AlertProps, "severity">;
    SnackbarProps?: SnackbarProps;
    timestamp: number;
};

type AlertOptions = {
    AlertProps?: Omit<AlertProps, "severity">;
    SnackbarProps?: SnackbarProps;
};

type IAlertContext = {
    alertUser: (alert: Message, options?: AlertOptions) => void;
    onClose: () => void;
    alert: Alert | null;
};

const AlertContext = React.createContext<IAlertContext>({
    alertUser: () => null,
    onClose: () => null,
    alert: null,
});

export function useAlert(): IAlertContext {
    return useContext(AlertContext);
}

const Alert: React.FC = () => {
    const { alert, onClose } = useAlert();

    return (
        <Snackbar
            key={alert?.timestamp}
            open={Boolean(alert?.message)}
            autoHideDuration={5000}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            {...alert?.SnackbarProps}
        >
            <MuiAlert severity={alert?.message.severity} {...alert?.AlertProps}>
                {alert?.message.title && <AlertTitle>{alert.message.title}</AlertTitle>}
                {alert?.message.value}
            </MuiAlert>
        </Snackbar>
    );
};

export const AlertProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const [alert, setAlert] = React.useState<Alert | null>(null);

    function alertUser(message: Message, options?: AlertOptions) {
        setAlert({ message, timestamp: new Date().getTime(), ...options });
    }

    function handleClose() {
        setAlert(null);
    }

    return (
        <AlertContext.Provider value={{ alertUser, alert, onClose: handleClose }}>
            {children}
            <Alert />
        </AlertContext.Provider>
    );
};
